import { API_ENDPOINTS, API_PUBLIC_AUTH_TOKEN } from "../constants/api";
import {
  handleAPIErrorBasedOnRcode,
  RCODE_ERROR_PAGES,
} from "../utils/UtilsRcode";
import {
  getCachedMaintenanceState,
  isMaintenanceConfigLoaded,
} from "../hooks/maintenance-page-hooks";
import { summaryDefaults } from "../data/defaults";
import { RESTRICTED } from "../constants/storage";

const Http = {
  getHeaders: async (extraHeaders?: any) => {
    const defaultHeaders = {
      Pragma: "no-cache",
      "Cache-Control": "no-cache",
      "x-sc-useragent": ";;WebPortal=0.1;Windows NT 6.1; WOW64;",
      "Content-type": "application/json;charset=UTF-8",
      "x-sc-client-os-version": 27,
    };

    return extraHeaders
      ? { ...defaultHeaders, ...extraHeaders }
      : defaultHeaders;
  },

  /**
   * Checks if the API call should be aborted based on maintenance mode and user restrictions
   * @param url The API endpoint URL
   * @returns true if the call should be aborted, false otherwise
   */
  shouldAbortApiCall: (url: string): boolean => {
    try {
      // Check if maintenance config has been loaded yet
      const isConfigLoaded = isMaintenanceConfigLoaded();

      if (!isConfigLoaded) {
        // Block all API calls except the maintenance config endpoint until it's loaded
        if (
          !url.includes(API_ENDPOINTS.MAINTENANCE_CONFIG) &&
          !url.includes(API_ENDPOINTS.GEOIP)
        ) {
          console.warn("API call blocked: Maintenance mode is on", url);
          return true;
        }
      }

      // Check if maintenance mode is enabled by accessing the cached state from useMaintenance hook
      const cachedState = getCachedMaintenanceState();
      const maintenanceEnabled = cachedState?.enabled || false;

      if (maintenanceEnabled) {
        // Allow maintenance config endpoint to proceed
        if (!url.includes(API_ENDPOINTS.MAINTENANCE_CONFIG)) {
          console.warn("API call blocked during maintenance:", url);
          return true;
        }
      }

      // Check if user is restricted
      const isUserRestricted = sessionStorage.getItem(RESTRICTED) === "true";

      if (isUserRestricted) {
        // Check if URL contains restricted paths
        const restrictedPaths = ["/summary", "/service"];
        const hasRestrictedPath = restrictedPaths.some((path) =>
          url.includes(path)
        );

        return hasRestrictedPath;
      }

      return false;
    } catch (error) {
      console.error("Error checking API call restrictions:", error);
      // If there's an error checking restrictions, allow the call to proceed
      return false;
    }
  },

  get: async (url: string, extraHeaders?: any, isPublic?: false) => {
    // Check if API call should be aborted due to maintenance or user restrictions
    if (Http.shouldAbortApiCall(url)) {
      // Return empty response as mentioned in the existing comment
      if (url.includes(API_ENDPOINTS.SUMMARY)) return summaryDefaults;
      return {};
    }

    const headers = await Http.getHeaders(extraHeaders);
    const response = await fetch(url, {
      method: "GET",
      headers: isPublic
        ? { Authorization: API_PUBLIC_AUTH_TOKEN }
        : { ...headers },
    });
    const responseData = await response.json();

    if (RCODE_ERROR_PAGES.includes(responseData.rcode)) {
      handleAPIErrorBasedOnRcode(responseData.rcode);
    }

    return responseData;
  },

  post: async (
    url: string,
    data?: any,
    extraHeaders?: any,
    controller?: any
  ) => {
    try {
      if (Http.shouldAbortApiCall(url)) {
        return {};
      }
      const headers = await Http.getHeaders(extraHeaders);
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
        ...controller,
      });

      const responseData = await response.json();

      // handle http errors
      if (!response.ok && !responseData.rcode) {
        const errorText = await response.text(); // await to read the response text
        throw new Error(`HTTP: ${response.status} - ${errorText}`);
      }

      // handle rcodes error
      if (responseData.rcode > 0) {
        handleAPIErrorBasedOnRcode(responseData.rcode);
        return Promise.reject(responseData);
      }

      return responseData;
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
};

export default Http;
